import { ChertoExperiments, type ExperimentConfig } from 'constants/experiments'

export type ExperimentEntry = [string, App.OptimizelyExperimentDetails]
export type ChertoExperimentEntry = [string, App.ChertoExperimentDetails | undefined, ExperimentConfig]
export type LabCampaignFlagEntry = [string, App.LabFeatureFlagResult | undefined]
export type LabRemoteFlagEntry = [string, App.LabRemoteConfigResult | undefined]
export type FeatureFlagEntry = [string, boolean]

const CHERTO_EXPERIMENTS_BY_ID = Object.values(ChertoExperiments).reduce<Record<string, ExperimentConfig>>(
  (experimentsById, experiment) => {
    experimentsById[experiment.id] = experiment
    return experimentsById
  },
  {},
)

/**
 * Formats the experiment title by splitting on underscores and hyphens.
 * e.g. 'multi_item_cart' -> 'Multi Item Cart', 'checkout-copy' -> 'Checkout Copy'
 */
export function formatExperimentTitle(experimentId: string): string {
  const experimentIdList = experimentId.split(/[_-]/)
  if (experimentIdList.length === 0) {
    return ''
  }
  return experimentIdList
    .filter(word => word.length > 0)
    .map(word => word[0].toUpperCase() + word.slice(1))
    .join(' ')
}

/** @deprecated Use formatExperimentTitle */
export const formatOptimizelyTitle = formatExperimentTitle

export function filterExperimentEntry(query: string) {
  return ([itemId]: ExperimentEntry | ChertoExperimentEntry | LabCampaignFlagEntry | LabRemoteFlagEntry | FeatureFlagEntry) => {
    const itemName = formatExperimentTitle(itemId)
    return (itemId.toLowerCase().includes(query.toLowerCase()) || itemName.toLowerCase().includes(query.toLowerCase()))
  }
}

/** @deprecated Use filterExperimentEntry */
export const filterOptimizelyEntry = filterExperimentEntry

export function getChertoExperimentConfig(experimentId: string): ExperimentConfig | undefined {
  return CHERTO_EXPERIMENTS_BY_ID[experimentId]
}

export function getChertoExperimentCohortKeys(variations: ReadonlyArray<string>): Array<string> {
  return ['off', ...variations]
}

export function getChertoSelectedCohort(
  experiment: App.ChertoExperimentDetails | undefined,
  cohortKeys: Array<string>,
): string {
  if (!experiment?.enabled) {
    return 'off'
  }

  if (cohortKeys.includes(experiment.variationKey)) {
    return experiment.variationKey
  }

  return cohortKeys[0] ?? experiment.variationKey
}

export function getExperimentCohortKeys(experiment: App.OptimizelyExperimentDetails): Array<string> {
  const variationMap = experiment.variation ?? {}
  const cohortKeys = Object.keys(variationMap).sort((a, b) => a.localeCompare(b))

  if (cohortKeys.length === 0) {
    return ['off', 'on']
  }

  return ['off', ...cohortKeys]
}

export function getSelectedCohort(
  experiment: App.OptimizelyExperimentDetails,
  cohortKeys: Array<string>,
): string {
  if (!experiment.enabled) {
    return 'off'
  }

  if (cohortKeys.includes(experiment.variationKey)) {
    return experiment.variationKey
  }

  if (cohortKeys.includes('on')) {
    return 'on'
  }

  return cohortKeys[0] ?? experiment.variationKey
}

export function getChertoExperimentReportUrl(experimentId: string): string {
  return `https://app.getcherto.com/experiments/${experimentId}`
}

export function formatLabPayloadJson(payload: unknown): string {
  return JSON.stringify(payload, null, 2)
}

export function hasLabPayload(payload: unknown): boolean {
  return payload !== undefined && payload !== null
}

export function openLabPayloadInNewTab(title: string, payload: unknown): void {
  const content = `${title}\n\n${formatLabPayloadJson(payload)}`
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export function getLabRemoteConfigPayload(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): unknown | undefined {
  if (!remoteConfig || remoteConfig.error || remoteConfig.value === undefined) {
    return undefined
  }

  return remoteConfig.value
}

function formatLabRemoteConfigError(
  error: NonNullable<App.LabRemoteConfigResult['error']>,
): string {
  if (error.type === 'NOT_FOUND') {
    return `${error.type}: ${error.key}`
  }

  return `${error.type}: ${error.message}`
}

export function getLabRemoteConfigStatusMessage(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): string | undefined {
  if (!remoteConfig) {
    return 'Not loaded'
  }

  if (remoteConfig.error) {
    return formatLabRemoteConfigError(remoteConfig.error)
  }

  if (remoteConfig.value === undefined) {
    return 'No value'
  }

  return undefined
}
